import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/pages/Applications";
import { IntegrityFinding } from "@/components/AiReview";

// Evaluators vs the second reader (AI): headline numbers, both rankings joined by lines (a line that
// crosses the cut-off changes the outcome), the largest gaps, and the integrity findings of the
// applicants still in consideration. Data: GET /ai-reviews.

const COLORS = { both_in: "#006BA2", both_out: "#C9D3D9", ev_in: "#E3120B", ai_in: "#379A8B" };
const IN_CONSIDERATION = ["recommended", "shortlisted"];

function corr(x, y) {
  const n = x.length;
  if (n < 2) return null;
  const mx = x.reduce((a, b) => a + b, 0) / n, my = y.reduce((a, b) => a + b, 0) / n;
  let sxy = 0, sxx = 0, syy = 0;
  for (let i = 0; i < n; i++) { sxy += (x[i] - mx) * (y[i] - my); sxx += (x[i] - mx) ** 2; syy += (y[i] - my) ** 2; }
  return sxx && syy ? sxy / Math.sqrt(sxx * syy) : null;
}

function SlopeChart({ rows, places, onOpen }) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(null);
  const n = rows.length;
  const ROW = 15, TOP = 28, W = 760, XL = 260, XR = 500;
  const H = TOP + n * ROW + 10;
  const y = (rank) => TOP + (rank - 0.5) * ROW;
  const order = ["both_out", "both_in", "ev_in", "ai_in"];
  const sorted = [...rows].sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind));
  const label = (r, side) => {
    const active = hover === r.id;
    const strong = active || r.kind === "ev_in" || r.kind === "ai_in";
    const dim = hover && !active;
    return {
      fontSize: 11, fontWeight: strong ? 600 : 400, cursor: "pointer",
      fill: dim ? "#c4c9cc" : r.kind === "both_out" && !active ? "#8a8f93" : "#1f2421",
      textAnchor: side === "left" ? "end" : "start",
    };
  };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto select-none" data-testid="ai-slope-chart" onMouseLeave={() => setHover(null)}>
      <text x={XL} y={14} textAnchor="middle" fontSize="12" fontWeight="700" fill="#1f2421">{t("admin.ai.cmp.evaluators")}</text>
      <text x={XR} y={14} textAnchor="middle" fontSize="12" fontWeight="700" fill="#1f2421">AI</text>
      {places > 0 && places < n && (
        <g>
          <line x1={0} x2={W} y1={TOP + places * ROW} y2={TOP + places * ROW} stroke="#1f2421" strokeWidth="0.7" strokeDasharray="4 3" />
          <text x={4} y={TOP + places * ROW + 12} fontSize="10" fontStyle="italic" fill="#1f2421">{t("admin.ai.cmp.cutoff", { places })}</text>
        </g>
      )}
      {sorted.map((r) => {
        const active = hover === r.id;
        const dim = hover && !active;
        const strong = r.kind === "ev_in" || r.kind === "ai_in";
        return (
          <g key={r.id} onMouseEnter={() => setHover(r.id)} onClick={() => onOpen(r.id)} style={{ cursor: "pointer" }}>
            <line x1={XL} x2={XR} y1={y(r.evRank)} y2={y(r.aiRank)} stroke={COLORS[r.kind]}
              strokeWidth={active ? 3 : strong ? 2 : 1.1} opacity={dim ? 0.15 : 1} />
            <circle cx={XL} cy={y(r.evRank)} r={2.4} fill={COLORS[r.kind]} opacity={dim ? 0.2 : 1} />
            <circle cx={XR} cy={y(r.aiRank)} r={2.4} fill={COLORS[r.kind]} opacity={dim ? 0.2 : 1} />
            <text x={XL - 8} y={y(r.evRank) + 4} style={label(r, "left")}>{r.name} {r.evRank}</text>
            <text x={XR + 8} y={y(r.aiRank) + 4} style={label(r, "right")}>{r.aiRank} {r.name}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function AiComparison({ data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const both = useMemo(() => (data?.applications || []).filter((a) => a.ai && a.evaluators), [data]);
  const defaultPlaces = useMemo(() => {
    const k = both.filter((a) => IN_CONSIDERATION.includes(a.status)).length;
    return k || Math.round(both.length / 2);
  }, [both]);
  const [placesInput, setPlacesInput] = useState("");
  const places = Math.max(0, Math.min(both.length, parseInt(placesInput, 10) || defaultPlaces));

  const rows = useMemo(() => {
    const name = (a) => `${a.name || ""} ${a.surname || ""}`.trim();
    const ev = [...both].sort((a, b) => b.evaluators.overall - a.evaluators.overall || name(a).localeCompare(name(b)));
    const ai = [...both].sort((a, b) => b.ai.overall - a.ai.overall
      || b.ai.scores.financial_part - a.ai.scores.financial_part
      || b.ai.scores.overall_success - a.ai.scores.overall_success || name(a).localeCompare(name(b)));
    const evRank = Object.fromEntries(ev.map((a, i) => [a.id, i + 1]));
    const aiRank = Object.fromEntries(ai.map((a, i) => [a.id, i + 1]));
    return both.map((a) => {
      const inEv = evRank[a.id] <= places, inAi = aiRank[a.id] <= places;
      return {
        id: a.id, name: name(a), status: a.status, evRank: evRank[a.id], aiRank: aiRank[a.id],
        ai: a.ai.overall, ev: a.evaluators.overall, gap: a.ai.overall - a.evaluators.overall,
        integrity: a.ai.result?.integrity || [],
        kind: inEv && inAi ? "both_in" : !inEv && !inAi ? "both_out" : inAi ? "ai_in" : "ev_in",
      };
    });
  }, [both, places]);

  if (both.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl text-center py-20 text-muted-foreground text-sm">
        <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-40" /> {t("admin.ai.cmp.no_data")}
      </div>
    );
  }

  const r = corr(rows.map((x) => x.ai), rows.map((x) => x.ev));
  const meanGap = rows.reduce((s, x) => s + Math.abs(x.gap), 0) / rows.length;
  const count = (k) => rows.filter((x) => x.kind === k).length;
  const same = count("both_in") + count("both_out");
  const gaps = [...rows].sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap)).slice(0, 12);
  const flagged = rows.filter((x) => IN_CONSIDERATION.includes(x.status) && x.integrity.length)
    .sort((a, b) => a.name.localeCompare(b.name));
  const Stat = ({ value, label }) => (
    <div className="bg-card border border-border rounded-xl p-4">
      <p className="font-heading text-2xl font-semibold text-primary">{value}</p>
      <p className="text-xs text-muted-foreground mt-1 leading-snug">{label}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ai-comparison">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat value={rows.length} label={t("admin.ai.cmp.reviewed")} />
        <Stat value={r == null ? "—" : r.toFixed(2)} label={t("admin.ai.cmp.correlation")} />
        <Stat value={meanGap.toFixed(2)} label={t("admin.ai.cmp.mean_gap")} />
        <Stat value={`${same}/${rows.length}`} label={t("admin.ai.cmp.agree", { places })} />
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-2">
          <div>
            <h3 className="font-heading text-lg font-medium">{t("admin.ai.cmp.chart_title")}</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-xl">{t("admin.ai.cmp.chart_hint")}</p>
          </div>
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            {t("admin.ai.cmp.places")}
            <Input type="number" min={1} max={both.length} value={placesInput} placeholder={String(defaultPlaces)}
              onChange={(e) => setPlacesInput(e.target.value)} className="w-20 h-8" data-testid="ai-places" />
          </label>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs my-3">
          {["both_in", "both_out", "ev_in", "ai_in"].map((k) => (
            <span key={k} className="inline-flex items-center gap-2">
              <span className="w-5 h-[3px] rounded" style={{ background: COLORS[k] }} />
              {t(`admin.ai.cmp.legend_${k}`)} ({count(k)})
            </span>
          ))}
        </div>
        <div className="max-w-3xl">
          <SlopeChart rows={rows} places={places} onOpen={(id) => navigate(`/admin/applications/${id}`)} />
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <h3 className="font-heading text-lg font-medium px-6 pt-5 pb-3">{t("admin.ai.cmp.gaps_title")}</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider text-left">
              <th className="py-3 px-6 font-medium">{t("admin.results.col_applicant")}</th>
              <th className="py-3 px-3 font-medium text-center">AI</th>
              <th className="py-3 px-3 font-medium text-center">{t("admin.ai.cmp.evaluators")}</th>
              <th className="py-3 px-3 font-medium text-center">{t("admin.ai.col_gap")}</th>
              <th className="py-3 px-3 font-medium text-center">{t("admin.ai.cmp.ranks")}</th>
              <th className="py-3 px-6 font-medium">{t("admin.results.col_status")}</th>
            </tr>
          </thead>
          <tbody>
            {gaps.map((x) => (
              <tr key={x.id} onClick={() => navigate(`/admin/applications/${x.id}`)} className="border-t border-border hover:bg-muted/30 cursor-pointer">
                <td className="py-3 px-6 font-medium">{x.name}</td>
                <td className="py-3 px-3 text-center">{x.ai.toFixed(1)}</td>
                <td className="py-3 px-3 text-center">{x.ev.toFixed(1)}</td>
                <td className={`py-3 px-3 text-center font-medium ${x.gap <= -1.5 ? "text-destructive" : x.gap >= 1.5 ? "text-primary" : ""}`}>
                  {x.gap > 0 ? "+" : "−"}{Math.abs(x.gap).toFixed(1)}
                </td>
                <td className="py-3 px-3 text-center text-muted-foreground">{x.evRank} → {x.aiRank}</td>
                <td className="py-3 px-6"><StatusBadge status={x.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-heading text-lg font-medium flex items-center gap-2 mb-1">
          <AlertTriangle className="w-5 h-5 text-amber-700" /> {t("admin.ai.cmp.integrity_title")}
        </h3>
        <p className="text-xs text-muted-foreground mb-4">{t("admin.ai.cmp.integrity_hint")}</p>
        {flagged.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin.ai.cmp.no_integrity")}</p> : (
          <div className="space-y-5">
            {flagged.map((x) => (
              <div key={x.id}>
                <button onClick={() => navigate(`/admin/applications/${x.id}`)} className="flex items-center gap-3 mb-2 hover:text-primary">
                  <span className="font-medium">{x.name}</span><StatusBadge status={x.status} />
                  <span className="text-xs text-muted-foreground">AI {x.ai.toFixed(1)} · {t("admin.ai.cmp.evaluators")} {x.ev.toFixed(1)}</span>
                </button>
                <div className="space-y-2">{x.integrity.map((f, i) => <IntegrityFinding key={i} f={f} />)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
